import React from 'react';
import { useWindowDimensions } from 'react-native';
import {
  createBottomTabNavigator,
  type BottomTabBarProps,
} from '@react-navigation/bottom-tabs';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
  TabBriefcaseIcon,
  TabCalendarIcon,
  TabClockIcon,
  TabHomeIcon,
  TabMenuIcon,
  TabPersonIcon,
} from '../components/TabBarIcons';
import { WebSidebar } from '../components/WebSidebar';
import { REPORT_ACCESS_PERMISSIONS } from '../config/reportCapabilities';
import { PERMISSIONS } from '../constants/permissions';
import { routes, type MainTabParamList } from '../constants/routes';
import { useAuth } from '../context/AuthContext';
import { formatUnreadBadge } from '../context/NotificationCountContext';
import { usePendingRequestCount } from '../context/PendingRequestCountContext';
import { canViewTeamDashboard } from '../utils/approvalRoles';
import { useAppColors } from '../context/PreferencesContext';
import { webSidebarModeFor } from '../utils/responsiveLayout';
import { isWebPlatform } from '../utils/webLayout';
import { AttendanceHomeScreen } from '../screens/attendance/AttendanceHomeScreen';
import { AdminDashboardScreen } from '../screens/dashboard/AdminDashboardScreen';
import { DashboardScreen } from '../screens/dashboard/DashboardScreen';
import { TeamDashboardScreen } from '../screens/dashboard/TeamDashboardScreen';
import { EmployeeListScreen } from '../screens/employees/EmployeeListScreen';
import { AdminLeaveRequestListScreen } from '../screens/leave/AdminLeaveRequestListScreen';
import { LeaveHomeScreen } from '../screens/leave/LeaveHomeScreen';
import { MenuScreen } from '../screens/menu/MenuScreen';
import { OfficeCalendarScreen } from '../screens/calendar/OfficeCalendarScreen';
import { AttendanceReportScreen } from '../screens/reports/AttendanceReportScreen';

const Tab = createBottomTabNavigator<MainTabParamList>();

/** Icon + label area; bottom inset is added separately for system nav. */
const TAB_BAR_CONTENT_HEIGHT = 56;
const TAB_BAR_MIN_BOTTOM_PADDING = 8;

type TabBarIconProps = { color: string; focused: boolean; size?: number };

const homeTabIcon = ({ color }: TabBarIconProps) => <TabHomeIcon color={color} />;
const clockTabIcon = ({ color }: TabBarIconProps) => <TabClockIcon color={color} />;
const calendarTabIcon = ({ color }: TabBarIconProps) => (
  <TabCalendarIcon color={color} />
);
const personTabIcon = ({ color }: TabBarIconProps) => (
  <TabPersonIcon color={color} />
);
const briefcaseTabIcon = ({ color }: TabBarIconProps) => (
  <TabBriefcaseIcon color={color} />
);
const menuTabIcon = ({ color }: TabBarIconProps) => <TabMenuIcon color={color} />;

const railSidebar = (props: BottomTabBarProps) => (
  <WebSidebar {...props} mode="rail" />
);
const expandedSidebar = (props: BottomTabBarProps) => (
  <WebSidebar {...props} mode="expanded" />
);

export function MainTabNavigator() {
  const auth = useAuth();
  const palette = useAppColors();
  const insets = useSafeAreaInsets();
  const { width } = useWindowDimensions();
  /** Web tablet+ uses a sidebar; native and mobile web keep the bottom tab bar. */
  const sidebarMode = isWebPlatform() ? webSidebarModeFor(width) : 'none';
  /** 3-button nav has a large bottom inset; gesture nav is smaller. */
  const tabBarPaddingBottom = Math.max(
    insets.bottom,
    TAB_BAR_MIN_BOTTOM_PADDING,
  );
  /** HR/admin org dashboard — keep separate from the employee home UI. */
  const isAdmin = auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW);
  const showAdminDashboard =
    isAdmin && auth.hasPermission(PERMISSIONS.ATTENDANCE_VIEW);
  const showEmployeeDashboard =
    auth.hasPermission(PERMISSIONS.ATTENDANCE_VIEW) && !isAdmin;
  /** Team Lead dashboard — HR Admin layout, assigned employees only. */
  const showTeamDashboard = canViewTeamDashboard(auth.roles, auth.permissions);
  /** Personal attendance home — employees only. */
  const showEmployeeAttendance = showEmployeeDashboard;
  /** Org-wide daily attendance register — HR/admin only. */
  const showAdminAttendance =
    isAdmin && auth.hasAnyPermission([...REPORT_ACCESS_PERMISSIONS]);
  /** Team leave approvals — HR/admin only. */
  const showAdminLeave = isAdmin && auth.hasPermission(PERMISSIONS.LEAVE_APPROVE);
  /** Personal leave home — employees only. */
  const showEmployeeLeave =
    auth.hasPermission(PERMISSIONS.LEAVE_APPLY) && !showAdminLeave;
  /** Org holidays & working days — employees with attendance access. */
  const showEmployeeCalendar = showEmployeeDashboard;
  const roleKey = auth.activeRole?.roleCode || auth.roles.join('|') || 'session';
  const initialRouteName = showAdminDashboard
    ? routes.adminDashboard
    : showTeamDashboard
      ? routes.teamDashboard
      : showEmployeeDashboard
        ? routes.dashboard
        : routes.menu;
  const pendingRequests = usePendingRequestCount();
  const leaveTabBadge = showAdminLeave
    ? formatUnreadBadge(pendingRequests.leaveCount) ?? undefined
    : undefined;

  return (
    <Tab.Navigator
      key={roleKey}
      initialRouteName={initialRouteName}
      tabBar={
        sidebarMode === 'rail'
          ? railSidebar
          : sidebarMode === 'expanded'
            ? expandedSidebar
            : undefined
      }
      screenOptions={{
        headerShown: false,
        ...(sidebarMode !== 'none' ? { tabBarPosition: 'left' as const } : null),
        tabBarActiveTintColor: palette.primary,
        tabBarInactiveTintColor: palette.textMuted,
        tabBarStyle: {
          backgroundColor: palette.surface,
          borderTopColor: palette.border,
          height: TAB_BAR_CONTENT_HEIGHT + tabBarPaddingBottom,
          paddingTop: 6,
          paddingBottom: tabBarPaddingBottom,
          width: '100%',
        },
        tabBarLabelStyle: { fontSize: 11, fontWeight: '700' },
      }}
    >
      {showEmployeeDashboard ? (
        <Tab.Screen
          name={routes.dashboard}
          component={DashboardScreen}
          options={{
            title: 'Home',
            tabBarIcon: homeTabIcon,
          }}
        />
      ) : null}
      {showTeamDashboard ? (
        <Tab.Screen
          name={routes.teamDashboard}
          component={TeamDashboardScreen}
          options={{
            title: 'My Team',
            tabBarIcon: personTabIcon,
          }}
        />
      ) : null}
      {showAdminDashboard ? (
        <Tab.Screen
          name={routes.adminDashboard}
          component={AdminDashboardScreen}
          options={{
            title: 'Dashboard',
            tabBarIcon: homeTabIcon,
          }}
        />
      ) : null}
      {showEmployeeAttendance ? (
        <Tab.Screen
          name={routes.attendance}
          component={AttendanceHomeScreen}
          options={{
            title: 'Attendance',
            tabBarIcon: clockTabIcon,
          }}
        />
      ) : null}
      {showEmployeeCalendar ? (
        <Tab.Screen
          name={routes.calendarTab}
          component={OfficeCalendarScreen}
          options={{
            title: 'Calendar',
            tabBarIcon: calendarTabIcon,
          }}
        />
      ) : null}
      {showAdminAttendance ? (
        <Tab.Screen
          name={routes.attendanceReport}
          component={AttendanceReportScreen}
          options={{
            title: 'Attendance',
            tabBarIcon: calendarTabIcon,
          }}
        />
      ) : null}
      {auth.hasPermission(PERMISSIONS.EMPLOYEE_VIEW) ? (
        <Tab.Screen
          name={routes.employees}
          component={EmployeeListScreen}
          options={{ tabBarIcon: personTabIcon }}
        />
      ) : null}
      {showEmployeeLeave ? (
        <Tab.Screen
          name={routes.leave}
          component={LeaveHomeScreen}
          options={{ tabBarIcon: briefcaseTabIcon }}
        />
      ) : null}
      {showAdminLeave ? (
        <Tab.Screen
          name={routes.leave}
          component={AdminLeaveRequestListScreen}
          options={{
            tabBarIcon: briefcaseTabIcon,
            tabBarBadge: leaveTabBadge,
          }}
        />
      ) : null}
      <Tab.Screen
        name={routes.menu}
        component={MenuScreen}
        options={{ tabBarIcon: menuTabIcon }}
      />
    </Tab.Navigator>
  );
}
